import { useCallback, useContext } from "react";
import { useLocalization } from "@fluent/react";
import { Button, Row, Tooltip, Chip } from "@narmi/design_system";
import { catcat as cc, ContentCard } from "cerulean";
import Filters from "byzantine/src/filters";
import utils from "byzantine/src/utils";
import { countryCodeToFlag } from "byzantine/src/dbbl/utils/internationalPhoneNumber";
import { REVERSAL_REASON_LABELS } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import type { ReversalReason } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import type { PaginatedDualApproval } from "byzantine/src/dbbl/businessLogic/entities/dualApprovals/types";
import InstitutionSettingsContext from "../../contexts/InstitutionSettingsContext";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import styles from "../DualApproval.module.scss";
import parseTemplateName from "../../PaymentActivity/common/templateNameUtils";
import { downloadWireImportCSV } from "../../PaymentActivity/WireFileImport/helpers";

type TopCardProps = {
  dualApproval: PaginatedDualApproval;
  isPendingDrawer?: boolean;
  originalPayment?: API.ACHPayment | null;
};

type BulkPaymentTopCardProps = TopCardProps & {
  handleClickViewPayments?: () => void;
};

const ReversalDetails = ({ dualApproval }: TopCardProps) => {
  const { l10n } = useLocalization();

  return (
    <>
      <h5 className={styles.drawerLabelHeading}>
        {l10n.getString("label-approval-due-date")}
      </h5>
      <div className="margin--bottom--m">
        {dualApproval.expiredAt
          ? Filters.longMonthDayYear(dualApproval.expiredAt)
          : "—"}
      </div>
      <h5 className={styles.drawerLabelHeading}>
        {l10n.getString("label-reversal-reason")}
      </h5>
      <div>
        {dualApproval.payment?.reversalReason
          ? REVERSAL_REASON_LABELS[
              dualApproval.payment.reversalReason as ReversalReason
            ] || dualApproval.payment.reversalReason
          : "—"}
      </div>
    </>
  );
};

const PaymentScheduleDetails = ({
  dualApproval,
  isPendingDrawer = true,
}: TopCardProps) => {
  const { l10n } = useLocalization();
  const { ach_next_cutoff: achNextCutoff } = useContext(
    InstitutionSettingsContext
  );

  const isACH = Boolean(dualApproval.achId);
  const isScheduledACH = Boolean(dualApproval.scheduledACHId);
  const isOneTimeScheduledACH =
    isScheduledACH && dualApproval.schedule?.recurrence === 1;

  if (isScheduledACH) {
    if (isOneTimeScheduledACH && dualApproval.schedule?.scheduledDate) {
      return (
        <>
          <h5 className={styles.drawerLabelHeading}>
            {l10n.getString("label-scheduled-date")}
          </h5>
          <span>
            {Filters.longMonthDayYear(dualApproval.schedule.scheduledDate)}
          </span>
        </>
      );
    }

    const recurrence = dualApproval.schedule?.recurrence;
    const showCount = recurrence && recurrence < 100;
    let endString = l10n.getString("approval-drawer-value-indefinite");

    if (dualApproval.schedule?.endDate) {
      endString = Filters.longMonthDayYear(dualApproval.schedule.endDate);
    } else if (showCount) {
      endString = l10n.getString("approval-drawer-value-ends-count", {
        count: recurrence,
      });
    }

    const totalAmount = showCount
      ? Filters.currency(
          (recurrence *
            Filters.dollarsToPennies(dualApproval.dollarAmount)) as Cents
        )
      : "";

    return (
      <>
        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("approval-drawer-label-payment-schedule")}
        </h5>
        <div className="margin--bottom--m">
          {l10n.getString("approval-drawer-value-payment-schedule", {
            frequency: dualApproval.schedule?.frequency || "",
            startDate: dualApproval.schedule?.scheduledDate
              ? Filters.longMonthDayYear(dualApproval.schedule.scheduledDate)
              : "",
          })}
        </div>
        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("approval-drawer-label-schedule-ends")}
        </h5>
        <div className={cc({ "margin--bottom--m": Boolean(totalAmount) })}>
          {endString}
        </div>
        {showCount ? (
          <>
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString("approval-drawer-label-payment-count", {
                count: recurrence,
              })}
            </h5>
            <div>{totalAmount}</div>
          </>
        ) : null}
      </>
    );
  }

  return (
    <>
      <h5 className={styles.drawerLabelHeading}>
        {l10n.getString("approval-drawer-label-payment-schedule")}
      </h5>
      <span>
        {isPendingDrawer
          ? l10n.getString("approval-drawer-one-time-payment")
          : l10n.getString("history-drawer-one-time-payment")}
      </span>
      {isACH && isPendingDrawer && achNextCutoff ? (
        <Tooltip
          maxWidth="210px"
          text={l10n.getString("pending-approval-achcutoff-copy", {
            cutoff: new Date(achNextCutoff * 1000).toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            }),
          })}
        >
          <span
            className={cc(
              "fontSize--m narmi-icon-alert-circle margin--left--xs"
            )}
          />
        </Tooltip>
      ) : null}
    </>
  );
};

export const TopCard = ({
  dualApproval,
  isPendingDrawer = true,
  originalPayment,
}: TopCardProps) => {
  const { l10n } = useLocalization();
  let typeCopy = l10n.getString("approval-drawer-standard-ach-label");

  if (dualApproval.wireId) {
    typeCopy = l10n.getString("fedwire-label");
  } else if (dualApproval.internationalWireId) {
    typeCopy = l10n.getString("international-wire-label");
  } else if (dualApproval.achReversalId) {
    typeCopy = l10n.getString("approval-drawer-ach-reversal-label");
  }

  const { fxQuote } = dualApproval;
  const hasFxQuote = !!(fxQuote && fxQuote.convertedAmount);
  const parsedTemplateName = dualApproval.payment?.templateName
    ? parseTemplateName(dualApproval.payment.templateName)
    : null;

  const getFxDisplayValues = () => {
    if (!fxQuote || !fxQuote.convertedAmount) return null;
    const isUsdSend = fxQuote.specifiedCurrency === "USD";

    const foreignCurrency = isUsdSend
      ? fxQuote.convertedCurrency
      : fxQuote.specifiedCurrency;
    const foreignAmount = isUsdSend
      ? fxQuote.convertedAmount
      : fxQuote.specifiedAmount;
    const usdAmount = isUsdSend
      ? fxQuote.specifiedAmount
      : fxQuote.convertedAmount;

    const foreignCurrencyCode = foreignCurrency.substring(0, 2);

    return {
      headlinePrefix: isUsdSend ? "~" : "",
      headlineAmount: utils.currencyFormattedValue(
        foreignAmount,
        foreignCurrency
      ),
      headlineCurrency: foreignCurrency,
      headlineFlag: countryCodeToFlag(foreignCurrencyCode),
      sendAmount: utils.currencyFormattedValue(usdAmount, "USD"),
      sendFlag: countryCodeToFlag("US"),
      sendLabel: isUsdSend
        ? l10n.getString("label-currency-amount-send-exactly")
        : l10n.getString("label-currency-amount-send-estimate"),
      exchangeRate: (() => {
        const parsedRate = parseFloat(fxQuote.rate || "NaN");
        if (Number.isNaN(parsedRate)) return null;
        // Mimic useEffectiveConversionRate: invert when
        // converted_currency !== settlement_currency (USD)
        const effectiveRate =
          fxQuote.convertedCurrency !== fxQuote.settlementCurrency
            ? 1 / parsedRate
            : parsedRate;
        return `1 USD = ${effectiveRate.toFixed(4)} ${foreignCurrency}`;
      })(),
    };
  };

  const fxDisplay = hasFxQuote ? getFxDisplayValues() : null;

  return (
    <div className="margin--top--l">
      <ContentCard kind="bordered">
        <h5 className="fontSize--m fontWeight--normal margin--bottom--xs">
          {dualApproval.achReversalId
            ? l10n.getString("approval-drawer-reversal-title", {
                transactionType:
                  originalPayment?.transaction_type ||
                  dualApproval.transactionType ||
                  "credit",
                recipientName: dualApproval.recipientName,
              })
            : l10n.getString("approval-drawer-type-to-recipient", {
                paymentType: typeCopy,
                recipientName: dualApproval.recipientName,
              })}
        </h5>
        {hasFxQuote && fxDisplay ? (
          <h1 className="fontColor--primary">
            {fxDisplay.headlinePrefix}
            {fxDisplay.headlineAmount}{" "}
            <span className="fontSize--m">
              {fxDisplay.headlineFlag} {fxDisplay.headlineCurrency}
            </span>
          </h1>
        ) : (
          <h1 className="fontColor--primary">
            {Filters.currency(dualApproval.dollarAmount, {
              hasDecimal: true,
            })}
          </h1>
        )}
        {hasFxQuote && fxDisplay && (
          <>
            <hr className="margin--bottom--l margin--top--l" />
            <Row>
              <Row.Item>
                <h5 className={styles.drawerLabelHeading}>
                  {fxDisplay.sendLabel}
                </h5>
              </Row.Item>
              <Row.Item shrink>
                {fxDisplay.sendAmount} {fxDisplay.sendFlag} USD
              </Row.Item>
            </Row>
            {fxDisplay.exchangeRate && (
              <Row className="margin--top--s">
                <Row.Item>
                  <h5 className={styles.drawerLabelHeading}>
                    {l10n.getString(
                      "international-wire-form-currency-estimated-exchange-rate"
                    )}
                  </h5>
                </Row.Item>
                <Row.Item shrink>{fxDisplay.exchangeRate}</Row.Item>
              </Row>
            )}
          </>
        )}
        <hr className="margin--bottom--l margin--top--l" />
        {dualApproval.achReversalId ? (
          <ReversalDetails dualApproval={dualApproval} />
        ) : (
          <PaymentScheduleDetails
            dualApproval={dualApproval}
            isPendingDrawer={isPendingDrawer}
          />
        )}
        {dualApproval.internationalWireId && parsedTemplateName ? (
          <>
            <h5 className={cc("margin--top--m", styles.drawerLabelHeading)}>
              {l10n.getString("label-wire-template--template-used")}
            </h5>
            <div className={styles.templateNameRow}>
              <span className={styles.templateNameText}>
                {parsedTemplateName.displayName}
              </span>
              {parsedTemplateName.isDeleted && (
                <Chip
                  kind="warn"
                  label={l10n.getString("label-wire-template--deleted")}
                />
              )}
            </div>
          </>
        ) : null}
      </ContentCard>
    </div>
  );
};

export const BulkPaymentTopCard = ({
  dualApproval,
  handleClickViewPayments,
}: BulkPaymentTopCardProps) => {
  const { l10n } = useLocalization();
  const features = useUserFeatures();
  const { ach_payments_collect: achPaymentsCollectFeature } = features;

  const bulkAmount = achPaymentsCollectFeature
    ? dualApproval.bulk?.netTotalDollarsWithSign || "$0"
    : dualApproval.bulk?.netTotalDollarsWithoutSign || "$0";

  return (
    <div className="margin--top--l">
      <ContentCard kind="bordered">
        <h5 className="fontSize--m fontWeight--normal margin--bottom--xs">
          {dualApproval.bulk?.title}
        </h5>
        <h1 className="fontColor--primary">{bulkAmount}</h1>
        <hr className="margin--bottom--l margin--top--l" />

        {dualApproval.bulk?.paymentCreditCount &&
        dualApproval.bulk.paymentCreditCount > 0 ? (
          <>
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString("label-sending")}
            </h5>
            <div className="margin--bottom--m">
              {dualApproval.bulk.creditTotalDollars}{" "}
              {l10n.getString("via-payments-cta", {
                total: dualApproval.bulk.paymentCreditCount,
              })}
            </div>
          </>
        ) : null}

        {achPaymentsCollectFeature &&
        dualApproval.bulk?.paymentDebitCount &&
        dualApproval.bulk.paymentDebitCount > 0 ? (
          <>
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString("label-collecting")}
            </h5>
            <div className="margin--bottom--m">
              {dualApproval.bulk.debitTotalDollars}{" "}
              {l10n.getString("via-payments-cta", {
                total: dualApproval.bulk.paymentDebitCount,
              })}
            </div>
          </>
        ) : null}

        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("label-scheduled-date")}
        </h5>
        <div className="margin--bottom--m">
          {dualApproval.bulk?.longDateRange || dualApproval.bulk?.dateRange}
        </div>

        <h5 className={cc("margin--none", styles.drawerLabelHeading)}>
          {l10n.getString("label-bulk-details")}
        </h5>
        {/* TODO: wire up bulk payments dialog to view individual payments */}
        <Button
          kind="plain"
          size="s"
          onClick={handleClickViewPayments}
          testId="view-payments"
        >
          {l10n.getString("view-payments-cta", {
            total: dualApproval.bulk?.paymentCount || 0,
          })}
          <span className="narmi-icon-chevron-right" />
        </Button>
        {dualApproval.bulk?.groupPosting ? (
          <div className="fontSize--xs fontColor--primary">
            {l10n.getString("group-posting-copy")}
          </div>
        ) : null}
      </ContentCard>
    </div>
  );
};

export const WireImportTopCard = ({
  dualApproval,
  handleClickViewPayments,
}: BulkPaymentTopCardProps) => {
  const { l10n } = useLocalization();
  const handleDownloadFile = useCallback(async () => {
    const wireImportFileName = dualApproval.wireImport?.fileName;
    if (!wireImportFileName) {
      return;
    }
    let readableFileName = "wire_file.csv";
    if (dualApproval.wireImport?.description) {
      readableFileName = dualApproval.wireImport.description;
    }

    await downloadWireImportCSV(wireImportFileName, readableFileName);
  }, [dualApproval.wireImport?.description, dualApproval.wireImport?.fileName]);

  const formattedDollarAmount = Filters.currency(dualApproval.dollarAmount, {
    hasDecimal: true,
  });

  const formattedDollarAmountWithSign = Filters.currency(-dualApproval.dollarAmount as Dollars, {
    hasDecimal: true,
  });

  return (
    <div className="margin--top--l">
      <ContentCard kind="bordered">
        <h5 className="fontSize--m fontWeight--normal margin--bottom--xs">
          {dualApproval.wireImport?.description}
        </h5>
        <h1 className="fontColor--primary">{formattedDollarAmountWithSign}</h1>
        <hr className="margin--bottom--l margin--top--l" />

        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("label-sending")}
        </h5>
        <div className="margin--bottom--m">
          {formattedDollarAmount}{" "}
          {l10n.getString("via-payments-cta", {
            total: dualApproval.wireImport?.wireCount ?? 0,
          })}
        </div>

        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("label-scheduled-date")}
        </h5>
        <div className="margin--bottom--m">
          {l10n.getString("wire-import-processed-upon-approval")}
        </div>
        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("label-payment-method")}
        </h5>
        <div className="margin--bottom--m">
          {l10n.getString("wire-file-import-method-name")}
        </div>

        <h5 className={cc("margin--none", styles.drawerLabelHeading)}>
          {l10n.getString("label-details")}
        </h5>
        <Button
          kind="plain"
          size="s"
          onClick={handleClickViewPayments}
          testId="view-payments"
        >
          {l10n.getString("view-payments-cta", {
            total: dualApproval.wireImport?.wireCount || 0,
          })}
          <span className="narmi-icon-chevron-right" />
        </Button>
        <h5 className={cc("margin--none", styles.drawerLabelHeading)}>
          {l10n.getString("label-file")}
        </h5>
        
        <Button
          kind="plain"
          size="s"
          onClick={handleDownloadFile}
          disabled={!dualApproval.wireImport?.fileName}
          testId="wire-import-download-file"
        >
          <span className="narmi-icon-download" />
          <span className="margin--left--xs">
            {l10n.getString("label-download-file")}
          </span>
        </Button>
        
      </ContentCard>
    </div>
  );
};
